import React from "react";
import UseV11Document from "./documents/UseV11Document";
import BiztingTermsOfServiceV12Page from "./BiztingTermsOfServiceV12Page";
import {
    TERMS_V11_EFFECTIVE_AT,
    TERMS_V12_EFFECTIVE_AT,
    TERMS_V12_NOTICE_START_AT,
    getCurrentBiztingTermsRoute,
    getScheduledBiztingTermsRoute,
} from "./clauseVersionDates";

type BiztingTermsOfServiceV11PageProps = {
    forceCurrent?: boolean;
};

const BiztingTermsOfServiceV11Page = ({ forceCurrent = false }: BiztingTermsOfServiceV11PageProps) => {
    const now = new Date();
    const isTermsV11Effective = now >= TERMS_V11_EFFECTIVE_AT;
    const isTermsV12NoticeStarted = now >= TERMS_V12_NOTICE_START_AT;
    const isTermsV12Effective = now >= TERMS_V12_EFFECTIVE_AT;

    if (!forceCurrent && (isTermsV12Effective || isTermsV12NoticeStarted)) {
        return <BiztingTermsOfServiceV12Page />;
    }

    if (forceCurrent) {
        return (
            <UseV11Document
                mode={isTermsV12Effective ? "previous" : "current"}
                currentVersionRoute={getCurrentBiztingTermsRoute(now)}
                scheduledVersionRoute={getScheduledBiztingTermsRoute(now)}
            />
        );
    }

    return (
        <UseV11Document
            mode={isTermsV11Effective ? "current" : "scheduled"}
            modal={isTermsV11Effective ? undefined : {
                title: "이전 버전 대비 변경사항 요약",
                subtitle: "2024년 8월 시행 버전 → 2026년 5월 1일 시행 버전",
                ctaLabel: "현재 버전 확인하기",
                ctaRoute: "/clause/use/v1.0",
                content: (
                    <>
                        <div className="tw-mb-6">
                            <div className="tw-mb-3 tw-flex tw-items-center tw-gap-2 tw-text-[13.5px] tw-font-bold tw-text-[#081f4a]">
                                <span className="tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded tw-bg-[#1a6ef5] tw-text-[11px] tw-font-bold tw-text-white">1</span>
                                이용약관 변경 내용
                            </div>
                            <div className="tw-overflow-hidden tw-rounded-lg tw-border tw-border-slate-200">
                                <table className="tw-w-full tw-border-collapse tw-text-left tw-text-[13px]">
                                    <thead>
                                    <tr className="tw-bg-[#081f4a] tw-text-white">
                                        <th className="tw-w-[32%] tw-px-3 tw-py-2.5 tw-font-semibold">조항</th>
                                        <th className="tw-px-3 tw-py-2.5 tw-font-semibold">변경 내용</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {[
                                        ["제2조 (11)·(12)", "용어 정의", "N-Pass 및 정보주체 용어 신설", "신설", "#1a6ef5", "#e8f1ff"],
                                        ["제18조 (17)", "회원의 의무", "N-Pass 이용 시 7개 세부 의무 규정 신설", "신설", "#1a6ef5", "#e8f1ff"],
                                        ["제2장의2", "N-Pass 서비스", "제22조의2~4 전체 신설 (N-Pass 제공, 보안 서약, 이용 제한·해지)", "신설", "#1a6ef5", "#e8f1ff"],
                                        ["제31조 (3) ⑬", "이용 제한", "N-Pass 보안 서약 위반·개인정보 오남용 시 이용 제한 사유 추가", "추가", "#1a8a4a", "#e8f6ef"],
                                        ["제32조 (5)", "손해배상", "고객사의 N-Pass 개인정보 오남용 시 전적 책임 및 회사 면책 명시", "추가", "#1a8a4a", "#e8f6ef"],
                                        ["제33조 (7)", "면책조항", "N-Pass 개인정보 활용 과정의 손해에 대한 회사 면책 명시", "추가", "#1a8a4a", "#e8f6ef"]
                                    ].map(([clause, label, desc, badge, color, bg], index) => (
                                        <tr key={clause} className={index % 2 === 1 ? "tw-border-t tw-border-slate-200 tw-bg-slate-50" : "tw-border-t tw-border-slate-200"}>
                                            <td className="tw-px-3 tw-py-2.5 tw-align-top tw-font-semibold">{clause}<br/><span className="tw-text-[11.5px] tw-font-normal tw-text-slate-500">{label}</span></td>
                                            <td className="tw-px-3 tw-py-2.5">{desc} <span className="tw-ml-1 tw-inline-block tw-rounded-full tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-bold" style={{color, background: bg}}>{badge}</span></td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div>
                            <div className="tw-mb-3 tw-flex tw-items-center tw-gap-2 tw-text-[13.5px] tw-font-bold tw-text-[#081f4a]">
                                <span className="tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded tw-bg-[#1a6ef5] tw-text-[11px] tw-font-bold tw-text-white">2</span>
                                고객사 주요 준수 사항 (N-Pass 이용 시)
                            </div>
                            <ul className="tw-list-none">
                                {[
                                    "관리자 계정 2단계 인증(MFA) 필수 설정 및 접근 권한 최소화",
                                    "개인정보 목적 외 사용 금지 및 재제공·판매 금지",
                                    "다운로드 파일 암호화 및 사유 기록 의무",
                                    "파기 요청 시 5일 이내 복구 불가능한 방법으로 파기",
                                    "서비스 종료 시 보유 개인정보 5일 내 전량 파기",
                                    "위반 시 발생하는 모든 법적 책임은 고객사 부담"
                                ].map((item) => (
                                    <li key={item} className="tw-flex tw-gap-2 tw-py-2 tw-text-[13.5px] tw-leading-7 tw-text-slate-600">
                                        <span className="tw-text-[#1a6ef5]">▸</span>
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </>
                )
            }}
        />
    );
};

export default BiztingTermsOfServiceV11Page;
